import { videoUrl } from '@helpers/cloudinary';
import { getSrcs, imageUrl } from '@helpers/image';
import { Device, until } from '@helpers/media';
import { clamp } from '@helpers/utils';
import { isMediaVideo } from '@helpers/video';
import { withMotionMax } from '@hoc/withMotionMax';
import { CarouselDirection } from '@hooks/useCarousel';
import { useModalGallery } from '@hooks/useModalGallery';
import { ReactComponent as SvgPlay } from '@img/icons/play-circle-instagram.svg';
import { ReactComponent as SvgZoom } from '@img/icons/zoom.svg';
import CarouselProvider from '@stories/Components/Misc/Carousel/CarouselProvider/CarouselProvider';
import Controls from '@stories/Components/Misc/Carousel/Controls/Controls';
import SimpleTrack from '@stories/Components/Misc/Carousel/SimpleTrack/SimpleTrack';
import ModalGallery from '@stories/Components/Misc/ModalGallery/ModalGallery';
import ModalGalleryProvider from '@stories/Components/Misc/ModalGallery/ModalGalleryProvider/ModalGalleryProvider';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { useSwipeable } from 'react-swipeable';
import SwipeIndicator from '../SwipeIndicator/SwipeIndicator';
import S from './MediaGallery.styles';
const MediaGallery = ({ galleryMedia, resetCarousel }) => {
    const [activeImageIndex, setActiveImageIndex] = React.useState(0);
    const [activeHeight, setActiveHeight] = React.useState(0);
    const [modalOpen, setModalOpen] = React.useState(false);
    const mainImgRef = React.useRef(null);
    const activeMedia = galleryMedia[activeImageIndex];
    useModalGallery({ count: galleryMedia.length });
    const updateHeight = React.useCallback(() => {
        if (!mainImgRef.current) {
            return;
        }
        setActiveHeight(mainImgRef.current.getBoundingClientRect().height);
    }, []);
    React.useEffect(() => {
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [updateHeight]);
    React.useEffect(() => {
        if (activeImageIndex >= galleryMedia.length) {
            setActiveImageIndex(0);
        }
    }, [galleryMedia, activeImageIndex]);
    if (!activeMedia) {
        return null;
    }
    return (React.createElement(ModalGalleryProvider, { count: galleryMedia.length },
        React.createElement(S.Container, null,
            React.createElement(MainImage, { ref: mainImgRef, activeMedia: activeMedia, activeImageIndex: activeImageIndex, setActiveImageIndex: setActiveImageIndex, maxIndex: galleryMedia.length - 1, setModalOpen: setModalOpen },
                React.createElement(AnimatePresence, { exitBeforeEnter: true }, renderImage(activeMedia, true))),
            React.createElement(CarouselProvider, { count: galleryMedia.length, direction: CarouselDirection.Vertical, resetParam: resetCarousel, goTo: activeImageIndex - 1 },
                React.createElement(S.ThumbnailWrapper, { style: {
                        '--wrapperHeight': `${activeHeight}px`,
                    }, "data-count": galleryMedia.length },
                    React.createElement(S.CarouselWrapper, null,
                        React.createElement(SimpleTrack, null,
                            galleryMedia.map((media, i) => {
                                /* Skip first item (will be added to end of thumbnails */
                                if (i === 0) {
                                    return null;
                                }
                                return (React.createElement(S.CarouselItem, { key: `${media.filterValue}-${i}`, "data-active": i === activeImageIndex },
                                    React.createElement(S.CarouselImage, { onClick: () => setActiveImageIndex(i) }, renderImage(media))));
                            }),
                            React.createElement(S.CarouselItem, { "data-active": activeImageIndex === 0 },
                                React.createElement(S.CarouselImage, { onClick: () => setActiveImageIndex(0) }, renderImage(galleryMedia[0]))))),
                    React.createElement(Controls, { paginationTitle: "Thumbnail carousel controls", wrap: false }))),
            React.createElement(CarouselProvider, { count: galleryMedia.length, resetParam: resetCarousel, goTo: activeImageIndex - 1 },
                React.createElement(S.ThumbnailWrapperMobile, null,
                    React.createElement(S.CarouselWrapper, null,
                        React.createElement(SimpleTrack, null,
                            galleryMedia.map((media, i) => {
                                /* Skip first item (will be added to end of thumbnails */
                                if (i === 0) {
                                    return null;
                                }
                                return (React.createElement(S.CarouselItem, { key: `${media.filterValue}-${i}`, "data-active": i === activeImageIndex },
                                    React.createElement(S.CarouselImage, { onClick: () => setActiveImageIndex(i) }, renderImage(media))));
                            }),
                            React.createElement(S.CarouselItem, { "data-active": activeImageIndex === 0 },
                                React.createElement(S.CarouselImage, { onClick: () => setActiveImageIndex(0) }, renderImage(galleryMedia[0]))))),
                    React.createElement(Controls, { paginationTitle: "Thumbnail carousel controls", wrap: false })))),
        React.createElement(ModalGallery, { ariaLabel: "Dummy Modal Gallery", isOpen: modalOpen, openIndex: activeImageIndex, media: galleryMedia, closeOnBackground: true, showBackground: true, showClose: true, onClose: () => setModalOpen(false), onIndexChange: (index) => setActiveImageIndex(index) }),
        galleryMedia.length > 0 ? (React.createElement(React.Fragment, null, galleryMedia.map((item, i) => (React.createElement("link", { key: `modal-${i}-${isMediaVideo(item) ? item.videoId : item.src}`, rel: "preload", as: isMediaVideo(item) ? 'video' : 'image', href: isMediaVideo(item) ? videoUrl(item.videoId, { format: 'webm' }) : imageUrl(item, {}) }))))) : null));
    function renderImage(media, mainImage) {
        const sizes = mainImage
            ? `${until(Device.MobileLarge)} 100vw, ${until(Device.TabletLarge)} 75vw, ${until(Device.DesktopLarge)} 57.5vw, 918px`
            : `${until(Device.MobileLarge)} 24vw, ${until(Device.TabletLarge)} 25vw, ${until(Device.DesktopLarge)} 18.5vw, 307px`;
        const srcs = !isMediaVideo(media)
            ? getMediaSrcs(media, !!mainImage)
            : media.previewImage
                ? getMediaSrcs(media.previewImage, !!mainImage)
                : [videoUrl(media.videoId, { format: 'jpg' })];
        const variants = {
            hidden: { opacity: 0 },
            visible: { opacity: 1 },
        };
        const variantAttrs = {
            initial: 'hidden',
            animate: 'visible',
            exit: 'hidden',
            variants,
        };
        return isMediaVideo(media) ? (React.createElement(React.Fragment, null,
            React.createElement(S.Image, { key: srcs[0].split(' ')[0], sizes: sizes, src: srcs[0].split(' ')[0], srcSet: srcs.join(','), alt: media.title ?? undefined, role: !media.title ? 'presentation' : undefined, loading: "eager", ...variantAttrs }),
            React.createElement(SvgPlay, { "data-play": true }))) : (React.createElement(S.Image, { key: srcs[0].split(' ')[0], sizes: sizes, src: srcs[0].split(' ')[0], srcSet: srcs.join(','), alt: media.altText ?? undefined, role: !media.altText ? 'presentation' : undefined, loading: "lazy", ...variantAttrs }));
    }
    function getMediaSrcs(image, mainImage) {
        return mainImage ? getSrcs(image, 918, 375, 8, 9 / 16) : getSrcs(image, 307, 88, 6, 9 / 16);
    }
};
const MainImage = React.forwardRef(({ children, ...props }, ref) => {
    const [hasSwiped, setHasSwiped] = React.useState(false);
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            props.setActiveImageIndex(props.activeImageIndex === props.maxIndex
                ? 0
                : clamp(props.activeImageIndex + 1, props.maxIndex));
            setHasSwiped(true);
        },
        onSwipedRight: () => {
            props.setActiveImageIndex(props.activeImageIndex === 0
                ? props.maxIndex
                : clamp(props.activeImageIndex - 1, props.maxIndex));
            setHasSwiped(true);
        },
    });
    return (React.createElement(S.MainImageWrapper, { ...swipeHandlers },
        React.createElement(S.MainImageButton, { onClick: () => props.setModalOpen(true), ref: ref, "aria-label": `Open ${isMediaVideo(props.activeMedia) ? 'video' : 'image'} in modal` },
            children,
            React.createElement(SvgZoom, { "data-zoom": true }),
            React.createElement(SwipeIndicator, { initialId: "media-galley", isVisible: !hasSwiped }))));
});
MainImage.displayName = 'MainImage';
export default withMotionMax(MediaGallery);
